import React, { useEffect, useRef, useState } from 'react';
import { animate, useReducedMotion } from 'framer-motion';
import { WEEKLY_REPORT_EXAMPLE as R } from '../data/site';
import ServiceMap from './ServiceMap';

/** Splits "$640" / "31" / "$28" into a prefix, the number to animate, and a suffix. */
const parseStat = (value: string): { prefix: string; n: number; suffix: string } => {
  const m = value.match(/^(\D*)(\d[\d,]*)(\D*)$/);
  if (!m) return { prefix: '', n: 0, suffix: value };
  return { prefix: m[1], n: Number(m[2].replace(/,/g, '')), suffix: m[3] };
};

const StatValue: React.FC<{ value: string; started: boolean; reduced: boolean }> = ({
  value,
  started,
  reduced,
}) => {
  const { prefix, n, suffix } = parseStat(value);
  const [display, setDisplay] = useState(reduced || !started ? n : 0);

  useEffect(() => {
    if (!started) return undefined;
    if (reduced) {
      setDisplay(n);
      return undefined;
    }
    const controls = animate(0, n, {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [started, n, reduced]);

  return (
    <>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </>
  );
};

export interface WeeklyReportProps {
  /**
   * When provided, the "New leads" stat renders this number directly instead
   * of running its own count-up tween. The hero passes this so the stat can
   * be driven lead-by-lead in step with the map pings landing, rather than
   * tweening on its own timeline. Omit it (the Marketing page does) and
   * "New leads" behaves exactly as it always has.
   */
  leadCount?: number;
  /**
   * When provided, this replaces the component's own IntersectionObserver as
   * the trigger for the rows / next-week / Approved sequence -- the hero
   * flips it to true once its own map-and-pings lead-in has finished, so the
   * report's existing timers start on cue instead of as soon as it scrolls
   * into view. Omit it and the component watches its own visibility exactly
   * as it always has.
   */
  sequenceStart?: boolean;
  /**
   * Round 2: the service-area map used to be a full-bleed layer behind the
   * whole hero report; it now lives INSIDE this component as the "Where
   * they came from" block's own visual (mostly hidden behind an opaque
   * card was the whole problem). Only the hero passes `showMap` and
   * `mapDrawIn` -- the Marketing page's report
   * renders none of these, so its markup is untouched.
   */
  showMap?: boolean;
  /** True once the map's line-draw should run (mirrors ServiceMap's own prop). */
  mapDrawIn?: boolean;
}

/**
 * The hero visual, now alive: stats count up once in view, then the source
 * rows reveal in order, then the next-week block, then a beat, then the
 * Approve chip presses once and settles into "Approved" with a timestamp.
 * One shot, no loop. `prefers-reduced-motion` renders the fully settled
 * state immediately, with no animation.
 *
 * `leadCount` / `sequenceStart` are optional escape hatches so the hero can
 * synchronize this component with its own map-and-pings sequence. Neither
 * prop is passed anywhere else (e.g. the Marketing page's report), so the
 * default, no-props behavior is unchanged.
 */
const WeeklyReport: React.FC<WeeklyReportProps> = ({
  leadCount,
  sequenceStart,
  showMap,
  mapDrawIn,
}) => {
  const reduced = Boolean(useReducedMotion());
  const rootRef = useRef<HTMLDivElement>(null);
  const externalControl = sequenceStart !== undefined;
  const [autoStarted, setAutoStarted] = useState(false);
  const [rowsIn, setRowsIn] = useState(false);
  const [blockIn, setBlockIn] = useState(false);
  const [approved, setApproved] = useState(false);

  const started = externalControl ? Boolean(sequenceStart) : autoStarted;

  // Derived, not seeded: useReducedMotion() can resolve after the first
  // render (it reads a media query in an effect), so seeding state from
  // `reduced` at construction time could leave a reduced-motion visitor
  // stuck on the pre-sequence frame if that resolves late. Deriving the
  // rendered state from `reduced` on every render instead guarantees the
  // settled state shows as soon as `reduced` becomes true, regardless of
  // when that happens.
  const showRows = reduced || rowsIn;
  const showBlock = reduced || blockIn;
  const showApproved = reduced || approved;

  useEffect(() => {
    if (externalControl) return undefined; // the parent owns `started`
    if (autoStarted || !rootRef.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setAutoStarted(true);
      },
      { threshold: 0.3 }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [autoStarted, externalControl]);

  // One-shot sequence, driven off `started`. Reduced-motion visitors never
  // run this timer effect at all (the `reduced` guard below), and always
  // render the settled state via the `show*` derived values above -- no
  // timers, no mid-flight frame is ever shown, regardless of when
  // `useReducedMotion()` resolves.
  useEffect(() => {
    if (!started || reduced) return undefined;
    const timers = [
      window.setTimeout(() => setRowsIn(true), 150),
      window.setTimeout(() => setBlockIn(true), 350),
      window.setTimeout(() => setApproved(true), 600),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [started, reduced]);

  const total = R.sources.reduce((sum, s) => sum + (parseStat(s.value).n || 0), 0) || 1;
  const newLeadsLabel = R.stats[0].label;

  const nextWeekContent = (
    <>
      <p className="wr__label">{R.nextLabel}</p>
      <p className="wr__nextline">{R.next}</p>
      <span className={`console__chip wr__chip${showApproved ? ' l1-approved' : ''}`}>
        {showApproved ? 'Approved' : 'Approve'}
        {showApproved && <span className="wr__chip-time">{R.approvedAt}</span>}
      </span>
    </>
  );

  // Row order matches ServiceMap's ping types, so each row carries the dot
  // style of its pings and the rows double as the map legend.
  const SOURCE_TYPES = ['instagram', 'google', 'walkin'] as const;

  const sourceRows = (
    <div className="console__data">
      {R.sources.map((row, i) => {
        const share = (parseStat(row.value).n / total) * 100;
        const srcType = SOURCE_TYPES[i] ?? 'walkin';
        return (
          <div
            className={`console__row wr__row wr__row--${srcType}${showRows ? ' l1-in' : ''}`}
            key={row.label}
            style={{ transitionDelay: reduced ? '0ms' : `${i * 50}ms` }}
          >
            <span>{row.label}</span>
            <span>{row.value}</span>
            <span className="wr__bartrack" aria-hidden="true">
              <span
                className="wr__bar"
                style={{
                  width: started ? `${share}%` : '0%',
                  transitionDuration: reduced ? '0ms' : undefined,
                  transitionDelay: reduced ? '0ms' : `${i * 70}ms`,
                }}
              />
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={`console wr${started ? ' wr--live' : ''}`}
      ref={rootRef}
      role="img"
      aria-label={`An example weekly report for ${R.client}: ${R.stats
        .map((s) => `${s.label} ${s.value}${s.note ? ` (${s.note})` : ''}`)
        .join(', ')}. ${R.sourcesLabel}: ${R.sources
        .map((s) => `${s.label} ${s.value}`)
        .join(', ')}. ${R.nextLabel}: ${R.next}${
        showApproved ? ` Suggestion approved ${R.approvedAt}.` : ''
      }`}
    >
      <div className="console__chrome" aria-hidden="true">
        <span className="console__title">{R.host}</span>
        <span className="console__badge wr__badge">Example</span>
      </div>

      <div className="console__body wr__body" aria-hidden="true">
        <div className="wr__title">
          <span className="wr__week">{R.week}</span>
          <span className="wr__client">{R.client}</span>
        </div>

        <div className="wr__stats">
          {R.stats.map((s) => (
            <div className="wr__stat" key={s.label}>
              <b className="wr__statvalue">
                {s.label === newLeadsLabel && leadCount !== undefined ? (
                  leadCount
                ) : (
                  <StatValue value={s.value} started={started} reduced={reduced} />
                )}
              </b>
              <span className="wr__statlabel">{s.label}</span>
              {s.note && <span className="wr__statnote">{s.note}</span>}
            </div>
          ))}
        </div>

        <div className="wr__block">
          <p className="wr__label">{R.sourcesLabel}</p>
          {showMap ? (
            <div className="wr__sourcesgrid">
              <ServiceMap
                drawIn={Boolean(mapDrawIn)}
                pingsLanded={leadCount ?? 0}
                reduced={reduced}
              />
              {/* Round 3: the right column stacks the rows AND the
                  next-week block so the map's height can match the whole
                  column instead of just the (much shorter) rows -- that's
                  what killed round 2's dead space under the map. */}
              <div className="wr__sourcescol">
                {sourceRows}
                <div className={`wr__nextblock${showBlock ? ' l1-in' : ''}`}>{nextWeekContent}</div>
              </div>
            </div>
          ) : (
            sourceRows
          )}
        </div>

        {!showMap && (
          <div className={`wr__nextblock${showBlock ? ' l1-in' : ''}`}>{nextWeekContent}</div>
        )}
      </div>

      <div className="console__foot" aria-hidden="true">
        <span>Example</span>
      </div>
    </div>
  );
};

export default React.memo(WeeklyReport);
